import React, { memo, useMemo } from "react";
import { Box } from "@mui/material";
import { noop } from "lodash";
import CommonUtils from "@shared/utils/CommonUtils";
import { Checkbox } from "@shared/components/Checkbox";
import { ListOptionBase, ListProps } from "@shared/components/List/models";
import { noOptions } from "@shared/components/List/constants";
import { useListSearch } from "@shared/components/List/useListSearch";
import Input from "@shared/components/Input/Input";
import SelfServiceListItem from "./SelfServiceListItem";

const SelfServiceList = <ListOption extends ListOptionBase>(props: ListProps<ListOption>) => {
  const { options, withSearch, size = "medium", onOptionClick } = props;

  const { searchValue, displayOptions, handleSearch } = useListSearch(options);

  const selectedOptionsMap = useMemo(() => {
    if (props.variant === "multiselect") {
      return CommonUtils.convertArrayToMap(props.selectedOptionIds);
    }
  }, [props]);

  return (
    <>
      {withSearch && !!options.length && (
        <Box pt={5} pb={3} px={5}>
          <Input search autoFocus value={searchValue} onChange={handleSearch} size="small" placeholder="Search" />
        </Box>
      )}
      {displayOptions.length ? (
        <Box data-testid="list-container">
          {displayOptions.map((option) => {
            let selected: boolean;

            if (props.variant === "multiselect") {
              selected = selectedOptionsMap![option.id as ListOption["id"]];
              option.startAdornment = <Checkbox checked={selected} />;
            } else {
              selected = props.selectedOptionId === option.id;
            }

            return (
              <SelfServiceListItem
                key={option.id}
                selected={selected}
                size={size}
                option={option}
                onOptionClick={onOptionClick}
              />
            );
          })}
        </Box>
      ) : (
        <Box data-testid="list-w-no-options">
          <SelfServiceListItem option={noOptions} size={size} onOptionClick={noop} />
        </Box>
      )}
    </>
  );
};

export default memo(SelfServiceList) as typeof SelfServiceList;
